/**
 * DeleteConfirmationDialog Component
 *
 * Confirmation dialog before soft-deleting a provider.
 */

import { AlertTriangle, Loader2 } from "lucide-react";
import type { ReactElement } from "react";

/** Dialog props */
export interface DeleteConfirmationDialogProps {
  /** Provider name to display */
  readonly providerName: string;
  /** Whether the delete operation is in progress */
  readonly isLoading: boolean;
  /** Called when user confirms deletion */
  readonly onConfirm: () => void;
  /** Called when user cancels */
  readonly onCancel: () => void;
}

/**
 * Confirmation dialog for soft-deleting a provider.
 */
export function DeleteConfirmationDialog({
  providerName,
  isLoading,
  onConfirm,
  onCancel,
}: DeleteConfirmationDialogProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-error/10 text-error">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold">Delete Provider</h2>
        </div>

        <p className="text-foreground-muted mb-2">
          Are you sure you want to delete provider <strong>{providerName}</strong>?
        </p>
        <p className="text-sm text-foreground-muted mb-6">
          This action will soft-delete the provider. It can be restored by an admin.
        </p>

        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={isLoading} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={isLoading} className="btn btn-error">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Provider"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
